import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@arcadeai/design-system/components/ui/atoms/collapsible";
import { ChevronRight } from "lucide-react";
import { useEffect, useState } from "react";
import { canUseDeepLink } from "./client-config";
import { ClientSelector } from "./components/client-selector";
import { ConfigDisplay } from "./components/config-display";
import { MCPInstallButton } from "./components/mcp-install-button";
import { resolveMcpClientForAuthType } from "./resolve-mcp-client";
import type {
  ConfigFormat,
  GatewayConnectGateway,
  McpAuthType,
  McpClientId,
} from "./types";

export type GatewayConnectSectionProps = {
  gateway: GatewayConnectGateway;
  enginePublicUrl: string;
  userId?: string;
  /**
   * Host-supplied URL for the API keys page. When provided, the MCP URL
   * auth hint links to it. Omit to render the hint without a link.
   */
  apiKeysHref?: string;
  /**
   * Fired with the effective client id whenever it changes — including on
   * mount and when the gateway's auth_type filters the previous selection
   * out. Lets hosts capture the initial client for telemetry without
   * waiting for the user to interact with the dropdown.
   */
  onClientChange?: (clientId: McpClientId) => void;
  onConfigCopy?: (
    clientId: McpClientId,
    format: ConfigFormat,
    authType: McpAuthType
  ) => void;
  onDeepLinkClick?: (clientId: McpClientId, gatewaySlug: string) => void;
  onDeepLinkError?: (clientId: McpClientId, error: unknown) => void;
};

export function GatewayConnectSection({
  gateway,
  enginePublicUrl,
  userId,
  apiKeysHref,
  onClientChange,
  onConfigCopy,
  onDeepLinkClick,
  onDeepLinkError,
}: GatewayConnectSectionProps) {
  const [selectedClient, setSelectedClient] = useState<McpClientId>(() =>
    resolveMcpClientForAuthType(undefined, gateway.auth_type)
  );

  const effectiveClient = resolveMcpClientForAuthType(
    selectedClient,
    gateway.auth_type
  );

  useEffect(() => {
    onClientChange?.(effectiveClient);
  }, [effectiveClient, onClientChange]);

  const showInstallBadge = canUseDeepLink(effectiveClient, gateway.auth_type);

  const configBlock = (
    <div className="space-y-4 rounded-lg border border-border/50 bg-muted/30 p-4">
      <ConfigDisplay
        apiKeysHref={apiKeysHref}
        authType={gateway.auth_type}
        clientId={effectiveClient}
        enginePublicUrl={enginePublicUrl}
        gatewayName={gateway.name}
        onCopy={onConfigCopy}
        slug={gateway.slug}
        userId={userId}
      />
    </div>
  );

  return (
    <div className="space-y-5">
      <ClientSelector
        authType={gateway.auth_type}
        onChange={setSelectedClient}
        value={effectiveClient}
      />

      {showInstallBadge ? (
        <>
          <MCPInstallButton
            clientId={effectiveClient}
            enginePublicUrl={enginePublicUrl}
            gatewayName={gateway.name}
            gatewaySlug={gateway.slug}
            onDeepLinkClick={onDeepLinkClick}
            onDeepLinkError={onDeepLinkError}
          />

          <Collapsible>
            <CollapsibleTrigger
              render={
                <Button
                  className="text-muted-foreground hover:text-foreground"
                  size="sm"
                  type="button"
                  variant="outline"
                />
              }
            >
              <ChevronRight
                aria-hidden
                className="size-3.5 shrink-0 transition-transform duration-200 group-aria-expanded/button:rotate-90"
              />
              Quick install not working? Set up manually
            </CollapsibleTrigger>
            <CollapsibleContent className="overflow-hidden data-closed:animate-collapsible-up data-open:animate-collapsible-down">
              <div className="h-(--collapsible-panel-height) pt-3 data-ending-style:h-0 data-starting-style:h-0">
                {configBlock}
              </div>
            </CollapsibleContent>
          </Collapsible>
        </>
      ) : (
        configBlock
      )}
    </div>
  );
}
